import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useAnchoredMenuPosition } from '../hooks/useAnchoredMenuPosition';
import type {
  ChartV2LegendMoreItem,
  RenderLegendTooltip,
} from '../types/chart-v2.types';
import { ChevronRightIcon, EllipsisIcon } from './Icons';
import styles from './Legend.module.scss';

interface MoreMenuItemRowProps {
  item: ChartV2LegendMoreItem;
  seriesId: string;
  isOpen: boolean;
  onHover: () => void;
  onActivate: () => void;
  onCloseAll: () => void;
}

const MoreMenuItemRow = ({
  item,
  seriesId,
  isOpen,
  onHover,
  onActivate,
  onCloseAll,
}: MoreMenuItemRowProps) => {
  const hasSubmenu = item.submenu !== undefined;

  return (
    <div className={styles.moreItemWrapper} onMouseEnter={onHover}>
      <button
        type="button"
        role="menuitem"
        className={clsx(
          styles.moreItem,
          item.disabled && styles.disabled,
          hasSubmenu && isOpen && styles.moreItemActive
        )}
        onClick={() => {
          if (item.disabled) {
            return;
          }

          if (hasSubmenu) {
            onActivate();
            return;
          }

          item.onClick?.();
          onCloseAll();
        }}
        disabled={item.disabled}
        aria-haspopup={hasSubmenu ? 'menu' : undefined}
        aria-expanded={hasSubmenu ? isOpen : undefined}
        data-testid={`chartv2-legend-more-${seriesId}-${item.id}`}
      >
        {item.leadingIcon && (
          <span className={styles.moreItemIcon}>{item.leadingIcon}</span>
        )}
        <span className={styles.moreItemLabel}>{item.label}</span>
        {hasSubmenu && (
          <span className={styles.moreItemChevron} aria-hidden>
            <ChevronRightIcon />
          </span>
        )}
      </button>
      {hasSubmenu && isOpen && (
        <div
          className={styles.subMenu}
          role="menu"
          data-testid={`chartv2-legend-more-submenu-${seriesId}-${item.id}`}
        >
          {item.submenu}
        </div>
      )}
    </div>
  );
};

export interface LegendMoreMenuProps {
  items: ChartV2LegendMoreItem[];
  seriesId: string;
  triggerTitle?: string;
  renderTooltip?: RenderLegendTooltip;
}

/**
 * The "more" (⋯) dropdown shown on each legend row's hover state. Holds
 * its own open / submenu state and tears the menu down on outside
 * click / Escape.
 */
export const LegendMoreMenu = ({
  items,
  seriesId,
  triggerTitle,
  renderTooltip,
}: LegendMoreMenuProps) => {
  const [open, setOpen] = useState(false);
  const [activeSubmenuId, setActiveSubmenuId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const position = useAnchoredMenuPosition({
    open,
    triggerRef: rootRef,
    menuRef,
  });

  useEffect(() => {
    if (!open) {
      setActiveSubmenuId(null);

      return undefined;
    }

    const handleClick = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) {
        return;
      }

      // The menu is portalled out of `rootRef`, so it needs its own check.
      if (
        rootRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }

      setOpen(false);
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const label = triggerTitle ?? 'More';
  const trigger = (
    <button
      type="button"
      className={styles.iconButton}
      aria-label={label}
      title={renderTooltip ? undefined : label}
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={() => setOpen(v => !v)}
      data-testid={`chartv2-legend-more-${seriesId}`}
    >
      <EllipsisIcon />
    </button>
  );

  return (
    <div className={styles.moreRoot} ref={rootRef}>
      {renderTooltip ? renderTooltip(trigger, label) : trigger}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.moreMenu}
            style={{ top: position.top, left: position.left }}
            role="menu"
            data-testid={`chartv2-legend-more-menu-${seriesId}`}
          >
            {items.map(item => (
              <MoreMenuItemRow
                key={item.id}
                item={item}
                seriesId={seriesId}
                isOpen={activeSubmenuId === item.id}
                onHover={() => {
                  // Disabled rows ignore hover — they shouldn't pop a
                  // submenu open just because the cursor passed over them.
                  if (item.disabled) {
                    return;
                  }

                  if (item.submenu !== undefined) {
                    setActiveSubmenuId(item.id);
                  } else {
                    setActiveSubmenuId(null);
                  }
                }}
                onActivate={() => setActiveSubmenuId(item.id)}
                onCloseAll={() => {
                  setOpen(false);
                  setActiveSubmenuId(null);
                }}
              />
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};
